import {
  addMonths,
  addQuarters,
  addYears,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { useUpdateUserSettings } from 'features/user';
import { useCallback, useState } from 'react';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useTodayScrollTime } from 'shared/hooks/use-today-scroll-time';
import { useUser } from 'shared/hooks/use-user';
import { LimitKeys } from 'shared/types/limit-keys';
import { TimelineTimeframes } from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { targetIsInput } from 'shared/utils/target-is-input';

const timeframeReferenceDateFunc: Record<
  TimelineTimeframes,
  (date: Timestamp) => Timestamp
> = {
  [TimelineTimeframes.Month]: startOfMonth,
  [TimelineTimeframes.Quarter]: startOfQuarter,
  [TimelineTimeframes.Year]: startOfYear,
  [TimelineTimeframes.FiveYears]: startOfYear,
};
const timeframeIncrementReferenceDateFunc: Record<
  TimelineTimeframes,
  (date: Timestamp, increment: number) => Timestamp
> = {
  [TimelineTimeframes.Month]: addMonths,
  [TimelineTimeframes.Quarter]: addQuarters,
  [TimelineTimeframes.Year]: addYears,
  [TimelineTimeframes.FiveYears]: addYears,
};

export const useCalendarControls = (captureKeys = true) => {
  const today = useToday();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const { allowed: isGoalsOverviewTimelineAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopGoalsOverviewTimeline,
    {},
  );
  const timeframe =
    user?.settings?.goalsOverview?.roadmap?.timeframe ??
    TimelineTimeframes.Year;

  const [referenceDate, setReferenceDate] = useState(
    timeframeReferenceDateFunc[timeframe](today),
  );

  // timeframe
  const onMonth = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    setReferenceDate(
      timeframeReferenceDateFunc[TimelineTimeframes.Month](today),
    );
    updateUserSettings({
      goalsOverview: { roadmap: { timeframe: TimelineTimeframes.Month } },
    });
  }, [isGoalsOverviewTimelineAllowed, today, updateUserSettings]);
  const onQuarter = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    setReferenceDate(
      timeframeReferenceDateFunc[TimelineTimeframes.Quarter](today),
    );
    updateUserSettings({
      goalsOverview: { roadmap: { timeframe: TimelineTimeframes.Quarter } },
    });
  }, [isGoalsOverviewTimelineAllowed, today, updateUserSettings]);
  const onYear = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    setReferenceDate(
      timeframeReferenceDateFunc[TimelineTimeframes.Year](today),
    );
    updateUserSettings({
      goalsOverview: { roadmap: { timeframe: TimelineTimeframes.Year } },
    });
  }, [isGoalsOverviewTimelineAllowed, today, updateUserSettings]);
  const onFiveYears = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    setReferenceDate(
      timeframeReferenceDateFunc[TimelineTimeframes.FiveYears](today),
    );
    updateUserSettings({
      goalsOverview: { roadmap: { timeframe: TimelineTimeframes.FiveYears } },
    });
  }, [isGoalsOverviewTimelineAllowed, today, updateUserSettings]);

  // change referenceDate
  const onToday = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }
    setReferenceDate(timeframeReferenceDateFunc[timeframe](today));
  }, [isGoalsOverviewTimelineAllowed, timeframe, today]);

  const onNext = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }

    setReferenceDate(
      timeframeIncrementReferenceDateFunc[timeframe](
        referenceDate,
        timeframe === TimelineTimeframes.FiveYears ? 5 : 1,
      ),
    );
  }, [isGoalsOverviewTimelineAllowed, referenceDate, timeframe]);

  const onPrev = useCallback(() => {
    if (!isGoalsOverviewTimelineAllowed) {
      return;
    }

    setReferenceDate(
      timeframeIncrementReferenceDateFunc[timeframe](
        referenceDate,
        timeframe === TimelineTimeframes.FiveYears ? -5 : -1,
      ),
    );
  }, [isGoalsOverviewTimelineAllowed, referenceDate, timeframe]);

  // handle keybindings
  useTodayScrollTime(captureKeys, onToday);

  useKeysDown(
    ['KeyM'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onMonth();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['KeyQ'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onQuarter();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['KeyY'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onYear();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['Digit5'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onFiveYears();
    },
    { otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['ArrowLeft'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onPrev();
    },
    { otherKeysInCombinationAllowed: true },
  );
  useKeysDown(
    ['ArrowRight'],
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      onNext();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return {
    referenceDate,
    timeframe,
    onMonth,
    onQuarter,
    onYear,
    onFiveYears,
    onToday,
    onNext,
    onPrev,
  };
};
